import type { UserPublicRead } from '@models/api';

/**
 * Shared prop shape for the two event-detail "who's going" face surfaces:
 * the authed page's EventGoingSummary and the guest hero's EventGoingPill.
 * Both render an overlapping avatar stack from `preview_users` plus a count,
 * and both self-hide when nobody is going.
 */
export interface GoingFacesProps {
  /** The going-set count. Pass `going_count` (NOT participant_count). */
  count: number;
  /** Real attendee preview (event.preview_users), creator first. */
  users?: UserPublicRead[];
}

export type GoingFacesFilter = 'named-only' | 'picture-or-name';

/** Event-detail's face cap — the default so those surfaces render unchanged. */
export const DEFAULT_MAX_FACES = 3;

/**
 * Face-selection rule for a going facepile (GoingFacepile renders from it), exported so a caller
 * composing copy from the same users (EventGoingSummary's name line) can't drift from the faces
 * it renders. `named-only` keeps users with a non-blank name (a name-led surface would otherwise
 * desync avatars from its name list); `picture-or-name` keeps anyone with a picture or a
 * non-blank name (a blank-name picture-less user folds into the count, not a hollow avatar).
 * Caps at `max` (default three, the event-detail pile size) — always the top of the list, which
 * the backend orders creator/host first.
 */
export function selectGoingFaces(
  users: UserPublicRead[] | undefined,
  filter: GoingFacesFilter,
  max: number = DEFAULT_MAX_FACES,
): UserPublicRead[] {
  return (users ?? [])
    .filter(user =>
      filter === 'named-only'
        ? user.full_name.trim().length > 0
        : Boolean(user.profile_picture) || user.full_name.trim().length > 0,
    )
    .slice(0, max);
}
